import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Wrap = styled.section`
  padding: 5rem 0 6rem;
  background: var(--bg-2);
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  scroll-margin-top: 80px;
`;

const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (min-width: 768px) { padding: 0 2rem; }
`;

const Head = styled.div`
  max-width: 720px;
  margin: 0 auto 3.5rem;
  text-align: center;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--mono);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 1.25rem;
  font-weight: 500;
`;

const Title = styled.h2`
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(2.1rem, 4.6vw, 3.6rem);
  line-height: 1.0;
  letter-spacing: -0.032em;
  margin: 0;
  color: var(--ink);

  .accent {
    font-family: var(--serif);
    font-weight: 400;
    font-style: italic;
    letter-spacing: -0.012em;
    color: var(--accent);
  }
`;

const Sub = styled.p`
  font-family: var(--sans);
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--ink-2);
  margin: 1.2rem auto 0;
  max-width: 54ch;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 720px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.25rem;
  }
`;

const Step = styled(motion.article)`
  background: var(--bg);
  border: 1px solid var(--rule);
  border-radius: 16px;
  padding: 1.75rem 1.5rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  box-shadow: var(--shadow-card);
  transition: border-color 280ms ease, box-shadow 320ms ease;

  &:hover {
    border-color: var(--rule-2);
    box-shadow: var(--shadow-card-hover);
  }
`;

const Index = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
`;

const Number = styled.span`
  font-family: var(--mono);
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  color: var(--ink-3);
  font-weight: 500;
`;

const IconBox = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--bg-2);
  border: 1px solid var(--rule);
  color: var(--ink);
  display: grid;
  place-items: center;

  svg { width: 16px; height: 16px; }
`;

const StepTitle = styled.h3`
  font-family: var(--sans);
  font-weight: 600;
  font-size: 1.18rem;
  letter-spacing: -0.018em;
  line-height: 1.2;
  margin: 0 0 0.7rem;
  color: var(--ink);
`;

const Body = styled.p`
  font-family: var(--sans);
  font-size: 0.95rem;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0;
`;

const reveal = (i) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
});

export default function HowItWorks() {
  return (
    <Wrap id="como-funciona">
      <Inner>
        <Head>
          <Eyebrow>Cómo funciona</Eyebrow>
          <Title>
            Conectamos tu tienda. <span className="accent">Conversamos por ti.</span>
          </Title>
          <Sub>
            Nos integramos a tu e-commerce y automatizamos las conversaciones
            por WhatsApp que generan recompra. Prácticas, medibles y
            personalizadas.
          </Sub>
        </Head>

        <Grid>
          <Step {...reveal(0)}>
            <Index>
              <Number>PASO 01</Number>
              <IconBox>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 7H7a4 4 0 100 8h2M15 17h2a4 4 0 100-8h-2M8 12h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </IconBox>
            </Index>
            <StepTitle>Conectamos tu tienda</StepTitle>
            <Body>
              Nos conectamos a tu e-commerce — Shopify, WooCommerce,
              PrestaShop, Jumpseller o cualquier otro que uses.
            </Body>
          </Step>

          <Step {...reveal(1)}>
            <Index>
              <Number>PASO 02</Number>
              <IconBox>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M21 12a9 9 0 11-3.5-7.1L21 7M21 4v3h-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </IconBox>
            </Index>
            <StepTitle>Conversamos por ti para generar ventas</StepTitle>
            <Body>
              Usamos IA conversacional para hacer que tus clientes vuelvan a
              comprarte, enviando recordatorios de re-compra, reactivaciones
              oportunas y recomendaciones precisas.
            </Body>
          </Step>

          <Step {...reveal(2)}>
            <Index>
              <Number>PASO 03</Number>
              <IconBox>
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 20V4M4 20h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <rect x="7"  y="11" width="3" height="6" rx="1" stroke="currentColor" strokeWidth="1.8" />
                  <rect x="12" y="7"  width="3" height="10" rx="1" stroke="currentColor" strokeWidth="1.8" />
                  <rect x="17" y="13" width="3" height="4" rx="1" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </IconBox>
            </Index>
            <StepTitle>Te enteras de quienes vuelven</StepTitle>
            <Body>
              En nuestro panel podrás ver las recompras, la recuperación de
              clientes inactivos y el feedback de los que no quieren volver.
              Decisiones con data, no con intuición.
            </Body>
          </Step>
        </Grid>
      </Inner>
    </Wrap>
  );
}
